import React, { useState, useEffect } from "react";
import { Cascader } from "antd";
import { FindOrganisationalUnitsQuery } from "~/gql/graphql";
import { FilterButtonGroup } from "@design-system";
import filterStyles from "../../../design-system/components/filter/filter.css";
import { VPDLabel } from "~/context/taxonomy-context";

interface Option {
  value: string | number;
  label: string;
  children?: Option[];
}

export type OrgUnitFilterProps = {
  selectedKeys: number[];
  setSelectedKeys: (selectedKeys: number[]) => void;
  confirm: () => void;
  clearFilters: () => void;
};

const { ["filter-dropdown"]: filterDropdownClassName, ["filter-content"]: filterContentClassName } =
  filterStyles;

export const filterOrgUnit = (
  practiceAreas: FindOrganisationalUnitsQuery["organisationalUnits"]
) => {
  const paOptions: Option[] =
    practiceAreas
      .filter((x) => !x.parent?.id)
      .map((pa) => {
        return {
          value: pa.id,
          label: pa.name,
          children: pa.children?.map((y) => ({ value: y.id, label: y.name })) || [],
        };
      }) || [];

  const OrgUnitFilter = ({
    selectedKeys,
    setSelectedKeys,
    confirm,
    clearFilters,
  }: OrgUnitFilterProps) => {
    const [selectedCascaderValue, setSelectedCascaderValue] = useState<number[]>([]);
    const [selectedOrgUnit, setSelectedOrgUnit] = useState<number[] | null>(null);

    const PA_LABEL = VPDLabel("PracticeArea");

    useEffect(() => {
      if (selectedKeys.length > 0) {
        const selectedOption = findOptionPath(paOptions, selectedKeys[0]);
        setSelectedCascaderValue(selectedOption);
        setSelectedOrgUnit([selectedKeys[0]]);
      }
    }, [selectedKeys]);

    return (
      <div className={filterDropdownClassName}>
        <FilterButtonGroup
          canApply={selectedOrgUnit !== null}
          canClear={selectedOrgUnit !== null}
          onClickApply={() => {
            if (selectedOrgUnit) {
              setSelectedKeys(selectedOrgUnit);
            }
            confirm();
          }}
          onClickClear={() => {
            clearFilters();
            setSelectedCascaderValue([]);
            setSelectedOrgUnit(null);
            confirm();
          }}
        />
        <div className={filterContentClassName}>
          <Cascader
            onChange={(val) => {
              if (!val) {
                clearFilters();
                setSelectedCascaderValue([]);
                setSelectedOrgUnit(null);
                return;
              }
              const values = val.map(Number) as number[];
              const last = values.length > 0 ? [values[values.length - 1]] : [];
              setSelectedCascaderValue(values);
              setSelectedOrgUnit(last);
            }}
            onClear={() => {
              setSelectedCascaderValue([]);
              setSelectedOrgUnit(null);
            }}
            value={selectedCascaderValue}
            placeholder={`Select ${PA_LABEL}`}
            options={paOptions}
            showSearch={true}
          />
        </div>
      </div>
    );
  };

  return OrgUnitFilter;
};

const findOptionPath = (options: Option[], targetValue: number): number[] => {
  for (const option of options) {
    if (option.value === targetValue) {
      return [targetValue];
    }
    if (option.children) {
      const childPath = findOptionPath(option.children, targetValue);
      if (childPath.length > 0) {
        return [option.value as number, ...childPath];
      }
    }
  }
  return [];
};
